import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Typography,
} from '@mui/material';
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactNode,
  RefAttributes,
  useEffect,
} from 'react';
import { Controller, useFormContext as useReactHookFormContext } from 'react-hook-form';
import { useSiloRealFormContext } from '../base/use-form-context';
import { useFormFieldStyles } from './use-form-field-styles';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type MultiboxFieldSx = {
  flex: number;
  minWidth: string;
  mt?: string;
};

type Option = string | { id: string; name: string };

const GREEN = '#6BAA00';
const GREEN_DARK = '#4F8000';
const GREEN_SOFT = '#EFF8E7';
const GREEN_BORDER = '#CFE4B6';
const TEXT_COLOR = '#2F4319';
const CHECKBOX_ICON_SIZE = 20;

type Props = {
  tid: string;
  name: string;
  options: Option[];
  required?: boolean;
  label?: string;
  labelIcon?: ReactNode;
  size?: 'medium' | 'large';
  exclusive?: boolean;
  direction?: 'horizontal' | 'vertical';
  pushOptionsToEnd?: boolean;
  clearOptionId?: string;
  onChange?: (selected: string[]) => void;
};

const getOptionId = (option: Option): string => {
  return typeof option === 'string' ? option : option.id;
};

const getOptionLabel = (option: Option): string => {
  return typeof option === 'string' ? option : option.name;
};

const renderEllipsisText = (text: string) => {
  return (
    <Box
      title={text}
      sx={{
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        '& .MuiTypography-root': {
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {text}
      </Typography>
    </Box>
  );
};

const renderCheckboxIcon = (checked: boolean, exclusive: boolean) => {
  return (
    <Box
      sx={{
        width: CHECKBOX_ICON_SIZE,
        height: CHECKBOX_ICON_SIZE,
        border: '1px solid',
        borderColor: checked ? GREEN : '#C7C7C7',
        borderRadius: exclusive ? '50%' : '5px',
        boxSizing: 'border-box',
        display: 'grid',
        placeItems: 'center',
        backgroundColor: checked ? GREEN : '#FFFFFF',
        color: '#FFFFFF',
      }}
    >
      {checked ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : null}
    </Box>
  );
};

type RenderClearSelectionButtonParams = {
  tid: string;
  show: boolean;
  onClick: () => void;
};

const renderClearSelectionButton = ({ tid, show, onClick }: RenderClearSelectionButtonParams) => {
  if (!show) {
    return (
      <Box
        aria-hidden
        sx={{
          alignSelf: 'stretch',
          flex: '0 0 42px',
          width: 42,
          minWidth: 42,
          borderLeft: '1px solid transparent',
        }}
      />
    );
  }

  return (
    <IconButton
      type="button"
      size="small"
      onClick={onClick}
      data-testid={`${tid}-clear-selection-btn`}
      aria-label="Borrar selección"
      sx={{
        alignSelf: 'stretch',
        flex: '0 0 42px',
        width: 42,
        minWidth: 42,
        height: 'auto',
        p: 0,
        borderLeft: '1px solid #F7C2C2',
        borderRadius: 0,
        color: '#EA1818',
        backgroundColor: '#FDE8E8',
        '&:hover': {
          backgroundColor: '#FDE8E8',
          boxShadow: '0 0 0 3px rgba(234, 24, 24, 0.12)',
        },
      }}
    >
      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
    </IconButton>
  );
};

type RenderOptionListParams = {
  tid: string;
  options: Option[];
  selectedValues: string[];
  direction: Props['direction'];
  exclusive: boolean;
  pushOptionsToEnd: boolean;
  onToggle: (optionId: string) => void;
};

const renderOptionList = ({
  tid,
  options,
  selectedValues,
  direction,
  exclusive,
  pushOptionsToEnd,
  onToggle,
}: RenderOptionListParams) => {
  const horizontal = direction === 'horizontal';

  return (
    <FormGroup
      row={horizontal}
      sx={{
        alignItems: horizontal && pushOptionsToEnd ? 'flex-end' : 'stretch',
        justifyContent: horizontal && pushOptionsToEnd ? 'flex-end' : 'flex-start',
        flexWrap: 'wrap',
        gap: 0.5,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      {options.map((option) => {
        const optionId = getOptionId(option);
        const optionLabel = getOptionLabel(option);
        const selected = selectedValues.includes(optionId);

        return (
          <FormControlLabel
            key={optionId}
            disableTypography
            label={renderEllipsisText(optionLabel)}
            control={
              <Checkbox
                size="small"
                checked={selected}
                icon={renderCheckboxIcon(false, exclusive)}
                checkedIcon={renderCheckboxIcon(true, exclusive)}
                data-testid={`${tid}-${optionId}`}
                onChange={() => onToggle(optionId)}
              />
            }
            sx={{
              flex: horizontal ? '0 1 auto' : '1 1 100%',
              minWidth: 0,
              maxWidth: '100%',
              minHeight: 32,
              m: 0,
              px: 0.75,
              py: 0.25,
              border: '1px solid',
              borderColor: selected ? GREEN_BORDER : 'transparent',
              borderRadius: '6px',
              color: selected ? GREEN_DARK : '#4F4A53',
              backgroundColor: selected ? GREEN_SOFT : 'transparent',
              whiteSpace: 'nowrap',
              transition: 'background-color 140ms ease, border-color 140ms ease, color 140ms ease',
              '&:hover': {
                borderColor: selected ? GREEN : GREEN_BORDER,
                backgroundColor: GREEN_SOFT,
              },
              '& .MuiCheckbox-root': {
                flex: '0 0 auto',
                p: 0,
                mr: 1,
              },
            }}
          />
        );
      })}
    </FormGroup>
  );
};

export const multibox: FR<HTMLDivElement, Props> = forwardRef((props, ref) => {
  const {
    tid,
    name = 'multibox',
    options,
    required = false,
    label,
    labelIcon,
    size,
    exclusive = false,
    direction = 'vertical',
    pushOptionsToEnd = false,
    clearOptionId,
    onChange,
  } = props;

  const { verify } = useSiloRealFormContext();
  useEffect(() => verify.uniqueness(name), []);

  const {
    control,
    formState: { errors },
  } = useReactHookFormContext();

  const hasError = Boolean(errors[name]);
  const errorMessage = errors[name]?.message as string | undefined;
  const fieldSx = useFormFieldStyles({ size }) as MultiboxFieldSx;
  const selectableOptionIds = options
    .filter((option) => getOptionId(option) !== clearOptionId)
    .map((option) => getOptionId(option));

  return (
    <FormControl
      ref={ref}
      sx={{
        ...fieldSx,
        maxWidth: '100%',
        boxSizing: 'border-box',
        display: 'grid',
        gap: 0,
      }}
      error={hasError}
      required={required}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        rules={{
          validate: (value) => {
            if (!required) return true;

            const selected = Array.isArray(value)
              ? (value as string[]).filter((entry) => selectableOptionIds.includes(entry))
              : [];
            return selected.length > 0 || 'Este campo es requerido';
          },
        }}
        render={({ field }) => {
          const selectableOptions = options.filter((option) => getOptionId(option) !== clearOptionId);
          const selectableOptionIds = selectableOptions.map((option) => getOptionId(option));

          const selectedValues = (Array.isArray(field.value) ? (field.value as string[]) : []).filter(
            (value) => selectableOptionIds.includes(value)
          );

          const selectedCount = selectedValues.length;
          const showClearSelection = selectedCount > 0;

          const toggleOption = (optionId: string): void => {
            const isSelected = selectedValues.includes(optionId);
            const isClearOption = clearOptionId === optionId;

            let nextValues: string[];

            if (isClearOption) {
              nextValues = [];
            } else if (exclusive) {
              nextValues = isSelected ? [] : [optionId];
            } else {
              nextValues = isSelected
                ? selectedValues.filter((value) => value !== optionId)
                : [...selectedValues.filter((value) => value !== clearOptionId), optionId];
            }

            field.onChange(nextValues);
            onChange?.(nextValues);
          };

          const clearSelection = (): void => {
            field.onChange([]);
            onChange?.([]);
          };

          const clearButton = renderClearSelectionButton({
            tid,
            show: showClearSelection,
            onClick: clearSelection,
          });

          const optionList = renderOptionList({
            tid,
            options: selectableOptions,
            selectedValues,
            direction,
            exclusive,
            pushOptionsToEnd,
            onToggle: toggleOption,
          });

          return (
            <Box
              data-testid={tid}
              role="group"
              aria-label={label ?? 'Opciones'}
              sx={{
                width: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: direction === 'horizontal' ? 'center' : 'flex-start',
                gap: 1,
                border: '1px solid',
                borderColor: hasError ? 'error.main' : showClearSelection ? GREEN : GREEN_BORDER,
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                color: TEXT_COLOR,
                overflow: 'hidden',
                transition: 'border-color 160ms ease, background-color 160ms ease',
                '&:hover': {
                  borderColor: GREEN,
                  backgroundColor: GREEN_SOFT,
                },
              }}
            >
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: direction === 'horizontal' ? 'center' : 'flex-start',
                  flexDirection: direction === 'horizontal' ? 'row' : 'column',
                  gap: direction === 'horizontal' ? 1 : 0.5,
                  px: 1.25,
                  py: 0.75,
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flex: '0 0 auto',
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      color: GREEN,
                      flex: '0 0 auto',
                      '& > svg': { fontSize: 20 },
                    }}
                  >
                    {labelIcon ?? <ChecklistRoundedIcon />}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 700,
                      color: TEXT_COLOR,
                    }}
                  >
                    {label ?? 'Seleccionar opciones'}
                    {required && (
                      <Box component="span" sx={{ color: '#EA1818', ml: 0.25 }}>
                        *
                      </Box>
                    )}
                  </Typography>
                </Box>

                {pushOptionsToEnd && direction === 'horizontal' ? (
                  <Box aria-hidden sx={{ flex: '1 1 auto' }} />
                ) : null}

                {optionList}
              </Box>

              {clearButton}
            </Box>
          );
        }}
      />

      <FormHelperText>{errorMessage}</FormHelperText>
    </FormControl>
  );
});

multibox.displayName = 'SiloReal-Form-Field-Multibox';
